import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material'
import {
  alpha,
  Box,
  Checkbox,
  createTheme,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  ThemeProvider,
  useTheme,
} from '@mui/material'
import {
  RichTreeView,
  TreeItem,
  treeItemClasses,
  useTreeViewApiRef,
} from '@mui/x-tree-view'
import { useRouteContext } from '~/routes'
import { bshSlice } from '~/store'
import { scrollToElement } from '~/utils'
import Scrollbar from './Scrollbar'
import Spinner from './Spinner'

const TreeContext = createContext({})
const TreeItemContext = createContext({})

/** Stable empty list for RichTreeView controlled props (avoid new [] each render). */
const EMPTY_ITEM_IDS = Object.freeze([])

const ProductTools = () => {
  const dispatch = useDispatch()
  const { archive } = useContext(TreeContext)
  const { product, selected } = useContext(TreeItemContext)

  const [archived, setArchived] = useState(!!product.archived_at)
  useEffect(() => {
    queueMicrotask(() => {
      setArchived(!!product.archived_at)
    })
  }, [product.archived_at])
  const [fetchingArchive, setFetchingArchive] = useState(false)

  const [status, setStatus] = useState(
    selected ? (product.html_status ?? 'empty') : undefined
  )
  useEffect(() => {
    queueMicrotask(() => {
      setStatus(selected ? (product.html_status ?? 'empty') : undefined)
    })
  }, [product.html_status, selected])

  const toggleArchive = useCallback(
    async (archive) => {
      setArchived(archive)
      setFetchingArchive(true)
      await dispatch(bshSlice.toggleArchive({ data: product }))
      setFetchingArchive(false)
    },
    [dispatch, product]
  )

  const [changingStatus, setChanginStatus] = useState(false)
  const changeStatus = useCallback(async () => {
    setChanginStatus(true)
    await dispatch(bshSlice.nextStatus({ data: product }))
    setChanginStatus(false)
  }, [dispatch, product])

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{ transform: 'translateY(-0.05em)' }}
    >
      <Box
        sx={{
          backgroundColor:
            product && !changingStatus && ['empty', 'done'].includes(status)
              ? `rgba(255,255,255,${status === 'empty' ? 0.5 : 1})`
              : undefined,
          border:
            !changingStatus && status === 'working'
              ? '2px solid #fff'
              : undefined,
          borderRadius: '100%',
          cursor: changingStatus ? 'default' : undefined,
          display: 'flex',
          width: '1rem',
          height: '1rem',
        }}
        onClick={
          product
            ? (e) => {
                e.stopPropagation()
                e.preventDefault()
                changeStatus()
              }
            : undefined
        }
      >
        {changingStatus && <Spinner delay={0} className="h-full" />}
      </Box>
      {archive &&
        (fetchingArchive ? (
          <Box className="w-[1.25rem] flex justify-center align-middle">
            <Spinner delay={0} className="w-[85%]" />
          </Box>
        ) : (
          <Checkbox
            checked={archived}
            color="default"
            sx={{
              p: 0,
              '& .MuiSvgIcon-root': { fontSize: '1.25rem !important' },
            }}
            onChange={(e) => {
              toggleArchive(e.target.checked)
            }}
          />
        ))}
    </Stack>
  )
}

const CustomTreeItem = (props) => {
  const tree = useContext(TreeContext)
  const selected = useMemo(() => {
    return tree.selected === props.itemId
  }, [tree.selected, props.itemId])
  const match = props.itemId.match(/^product\/(\d+)$/)
  const product = useSelector((state) =>
    bshSlice.productById(state, match ? match[1] : null)
  )
  return (
    <TreeItemContext.Provider value={{ ...props, product, selected }}>
      <TreeItem
        {...props}
        slots={product ? { icon: ProductTools } : undefined}
        sx={{
          color: (theme) => {
            if (!selected && product?.html_status === 'empty') {
              return theme.palette.inactive.text
            }
          },
          fontWeight:
            !product || selected || product?.html_status === 'done' ? 600 : 400,
        }}
        variant={product ? 'product' : 'category'}
      />
    </TreeItemContext.Provider>
  )
}

CustomTreeItem.propTypes = TreeItem.propTypes

const Tree = () => {
  const { t } = useTranslation()
  const route = useRouteContext(Tree.name)
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState([])
  const [selected, setSelected] = useState('')
  const [search, setSearch] = useState('')
  const [archive, setArchive] = useState(false)
  const tree = useSelector((state) =>
    bshSlice.treeByBrand(state, route.brand.url, archive, search)
  )

  const getStateFromRoute = useCallback(() => {
    if (!route.treePath) {
      return { selected: null, expanded: [] }
    }
    const expanded = [...route.treePath].map(
      ({ model, id }) => `${model}/${id}`
    )
    expanded.shift()
    const selected = expanded.length ? expanded[expanded.length - 1] : undefined
    if (selected?.model === 'product') {
      expanded.pop()
    }
    return { selected, expanded }
  }, [route.treePath])

  useEffect(() => {
    queueMicrotask(() => {
      const { selected, expanded } = getStateFromRoute()
      setSelected(selected ?? '')
      setExpanded(Array.isArray(expanded) ? expanded : [])
    })
  }, [getStateFromRoute])

  const rootTheme = useTheme()

  const theme = useMemo(
    () =>
      createTheme({
        ...rootTheme,
        components: {
          MuiTreeItem: {
            styleOverrides: {
              root: {
                [`& .${treeItemClasses.groupTransition}`]: {
                  marginLeft: '0.5rem',
                  paddingLeft: 0,
                  borderLeft: `1px dashed ${alpha(rootTheme.palette.text.primary, rootTheme.palette.mode === 'light' ? 0.2 : 0.1)}`,
                },
                [`& .${treeItemClasses.content}`]: {
                  borderRadius: '0',
                  paddingLeft: '0.5rem',
                  paddingRight: '0.75rem',
                  [`& .${treeItemClasses.label}`]: {
                    fontSize: '0.875rem',
                    fontWeight: 'inherit',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                  [`&.${treeItemClasses.selected}`]: {
                    backgroundColor: `${rootTheme.palette.primary.main} !important`,
                    color:
                      rootTheme.palette.mode === 'light'
                        ? rootTheme.palette.background.default
                        : 'inherit',
                    '&:hover': {
                      backgroundColor: rootTheme.palette.primary.main,
                    },
                  },
                },
              },
            },
            variants: [
              {
                props: { variant: 'category' },
                style: {
                  [`& > .${treeItemClasses.content} > .${treeItemClasses.iconContainer}`]:
                    {
                      display: 'none',
                    },
                },
              },
              {
                props: { variant: 'product' },
                style: {
                  [`& > .${treeItemClasses.content}`]: {
                    gap: '0.25rem',
                    paddingLeft: '0.25rem',
                    [`& > .${treeItemClasses.iconContainer}`]: {
                      width: 'auto',
                    },
                  },
                },
              },
            ],
          },
        },
      }),
    [rootTheme]
  )

  const handleSelect = useCallback(
    (event, itemIds) => {
      const itemId = Array.isArray(itemIds) ? itemIds[0] : itemIds
      if (!itemId) return
      let targetId = itemId
      if (
        route.treePath?.length >= 3 &&
        route.model === 'category' &&
        itemId === `${route.model}/${route.id}`
      ) {
        targetId = Object.values(
          route.treePath[route.treePath.length - 2]
        ).join('/')
      }
      navigate(`/${route.brand.url}/${route.action.path}/${targetId}`)
    },
    [
      navigate,
      route.action.path,
      route.brand.url,
      route.id,
      route.model,
      route.treePath,
    ]
  )

  const treeItems = tree ?? EMPTY_ITEM_IDS

  const expandedItems = useMemo(
    () => (Array.isArray(expanded) ? expanded : EMPTY_ITEM_IDS),
    [expanded]
  )

  const selectedItems = useMemo(() => {
    if (selected !== '') {
      return [selected]
    }
    return EMPTY_ITEM_IDS
  }, [selected])

  const getItemId = useCallback((item) => `${item.id}`, [])
  const getItemLabel = useCallback((item) => item.name || '<empty>', [])

  const treeViewSlots = useMemo(() => ({ item: CustomTreeItem }), [])

  const scrollBarContainerRef = useRef()

  const apiRef = useTreeViewApiRef()
  useEffect(() => {
    if (selected) {
      if (/^category\//.test(selected)) {
        queueMicrotask(() => {
          setSearch('')
        })
      }
      const itemElement = apiRef.current.getItemDOMElement(selected)
      if (itemElement) {
        const intId = setTimeout(() => {
          scrollToElement(itemElement, {
            offset: 0,
            scrollingElement: scrollBarContainerRef.current,
            block: 'nearest',
          })
        }, 500)
        return () => clearTimeout(intId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected])

  const intIdRef = useRef()
  const handleSearch = useCallback((value) => {
    if (value.length > 0 && value.length < 2) return
    clearTimeout(intIdRef.current)
    intIdRef.current = setTimeout(() => {
      setSearch(value)
    }, 500)
  }, [])
  useEffect(() => {
    return () => clearTimeout(intIdRef.current)
  }, [])

  useEffect(() => {
    queueMicrotask(() => {
      if (search) {
        const expanded = []
        const getIds = (items) => {
          if (!Array.isArray(items)) return
          items.forEach(({ id, children }) => {
            expanded.push(id)
            getIds(children)
          })
        }
        getIds(tree)
        setExpanded(expanded)
      } else {
        const { expanded } = getStateFromRoute()
        setExpanded(expanded)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const searchInputRef = useRef()
  useEffect(() => {
    if (!search) {
      searchInputRef.current.value = ''
    }
  }, [search])

  return (
    <TreeContext.Provider value={{ archive, selected }}>
      <Paper
        className="h-full flex flex-col w-[295px]"
        sx={{
          borderRadius: 0,
          zIndex: 1,
        }}
      >
        <Stack className="grow pt-1" spacing={1}>
          <Stack className="px-2" spacing={1}>
            <TextField
              inputRef={searchInputRef}
              placeholder={t('product.search')}
              size="small"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{ width: '2em', height: '2em', position: 'relative' }}
                    >
                      {search ? (
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                          }}
                          onClick={() => setSearch('')}
                        >
                          <CloseIcon />
                        </IconButton>
                      ) : (
                        <SearchIcon
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                          }}
                        />
                      )}
                    </InputAdornment>
                  ),
                },
              }}
              // value={search}
              variant="standard"
              onChange={(e) => handleSearch(e.target.value)}
            />
            <FormGroup className="px-1">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={archive}
                    onChange={(e) => setArchive(e.target.checked)}
                  />
                }
                label={
                  <span className="text-xs font-bold">
                    {t('archive.label')}
                  </span>
                }
              />
            </FormGroup>
          </Stack>
          <Box className="relative grow ms-1">
            <Scrollbar
              className="absolute inset-0 overflow-hidden"
              containerRef={(ref) => (scrollBarContainerRef.current = ref)}
              options={{ suppressScrollX: true }}
            >
              <ThemeProvider theme={theme}>
                <RichTreeView
                  apiRef={apiRef}
                  items={treeItems}
                  getItemId={getItemId}
                  getItemLabel={getItemLabel}
                  expandedItems={expandedItems}
                  selectedItems={selectedItems}
                  slots={treeViewSlots}
                  onSelectedItemsChange={handleSelect}
                />
              </ThemeProvider>
            </Scrollbar>
          </Box>
        </Stack>
      </Paper>
    </TreeContext.Provider>
  )
}

export default Tree
